import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PageContainer from "@/components/PageContainer";
import ForumService from "@/services/ForumService";
import { Eye, MessageCircle, ThumbsUp, Trash2 } from "lucide-react";
import useStore from "@/store";
import posthog from "posthog-js";

const formatRelativeTime = (value) => {
  if (!value) return "";
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return "";
  }
  const diffInSeconds = Math.floor((Date.now() - timestamp) / 1000);

  if (diffInSeconds < 60) {
    return "Just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} min${diffInMinutes === 1 ? "" : "s"} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks === 1 ? "" : "s"} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths === 1 ? "" : "s"} ago`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} year${diffInYears === 1 ? "" : "s"} ago`;
};

// Marks posts from the SpeechCraft system account so nobody mistakes them for a learner.
const OfficialBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-sm border border-primary/40 bg-primary/10 px-1.5 py-0.5 font-mono text-[9px] tracking-[0.15em] text-primary">
    OFFICIAL · AUTOMATED
  </span>
);

const OFFICIAL_AVATAR = { displayLetter: "🔊", colorClass: "bg-primary/15 text-primary" };

const Forum = () => {
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [threadsError, setThreadsError] = useState("");
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [isDetailView, setIsDetailView] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [threadForm, setThreadForm] = useState({ title: "", body: "" });
  const [threadFormError, setThreadFormError] = useState("");
  const [creatingThread, setCreatingThread] = useState(false);

  const [commentBody, setCommentBody] = useState("");
  const [commentError, setCommentError] = useState("");
  const [creatingComment, setCreatingComment] = useState(false);
  const [upvotingThreadId, setUpvotingThreadId] = useState(null);
  const [upvotingCommentId, setUpvotingCommentId] = useState(null);
  const [highlightedThreads, setHighlightedThreads] = useState({});
  const [highlightedComments, setHighlightedComments] = useState({});
  const [deletingThreadId, setDeletingThreadId] = useState(null);

  const { user } = useStore().user;
  const ADMIN_EMAIL = "dmenezes16@gmail.com";
  const isAdmin =
    (user?.email || "").toLowerCase() === ADMIN_EMAIL;

  const getAvatarMeta = useCallback((name) => {
    const palette = [
      "bg-primary/20 text-primary/80",
      "bg-primary/20 text-primary/80",
      "bg-elevated/20 text-fg-muted",
      "bg-accent/20 text-accent",
      "bg-elevated/20 text-fg-muted",
      "bg-accent/20 text-accent",
    ];
    const displayLetter = (name || "L").trim().charAt(0).toUpperCase() || "L";
    let hash = 0;
    for (let i = 0; i < displayLetter.length; i += 1) {
      hash = displayLetter.charCodeAt(i) + ((hash << 5) - hash);
    }
    const color = palette[Math.abs(hash) % palette.length];
    return { displayLetter, colorClass: color };
  }, []);

  useEffect(() => {
    posthog.capture("forum_page_viewed");
  }, []);

  const syncThreadCounts = useCallback((threadId, data) => {
    setThreads((prev) =>
      prev.map((thread) =>
        thread.id === threadId ? { ...thread, ...data } : thread
      )
    );
    setSelectedThread((prev) =>
      prev && prev.id === threadId ? { ...prev, ...data } : prev
    );
  }, []);

  const handleSelectThread = useCallback(
    async (threadId) => {
      if (!threadId) {
        setSelectedThread(null);
        setSelectedThreadId(null);
        setIsDetailView(false);
        return;
      }

      const existingThread =
        threads.find((thread) => thread.id === threadId) || null;
      if (existingThread) {
        setSelectedThread(existingThread);
      }

      setSelectedThreadId(threadId);
      setIsDetailView(true);
      setCommentError("");
      setSelectedLoading(true);

      try {
        const thread = await ForumService.getThread(threadId);
        setSelectedThread(thread);
        syncThreadCounts(threadId, {
          upvote_count: thread.upvote_count ?? 0,
          view_count: thread.view_count ?? 0,
          comment_count: thread.comments?.length ?? thread.comment_count ?? 0,
        });
      } catch (error) {
        console.error("Failed to load thread", error);
      } finally {
        setSelectedLoading(false);
      }
    },
    [syncThreadCounts, threads]
  );

  const loadThreads = useCallback(async () => {
    setThreadsLoading(true);
    setThreadsError("");
    try {
      const data = await ForumService.listThreads();
      setThreads(data);

      if (!data.length) {
        setSelectedThread(null);
        setSelectedThreadId(null);
        setIsDetailView(false);
      } else if (
        selectedThreadId &&
        !data.some((thread) => thread.id === selectedThreadId)
      ) {
        setSelectedThread(null);
        setSelectedThreadId(null);
        setIsDetailView(false);
      }
    } catch (error) {
      console.error("Failed to fetch threads", error);
      setThreadsError("Unable to load discussions right now.");
    } finally {
      setThreadsLoading(false);
    }
  }, [selectedThreadId]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    let timeout = null;
    const recordView = async () => {
      if (!isDetailView || !selectedThreadId) {
        return;
      }
      try {
        const response = await ForumService.recordView(selectedThreadId);
        if (response?.view_count !== undefined) {
          syncThreadCounts(selectedThreadId, {
            view_count: response.view_count,
          });
        }
      } catch (error) {
        console.error("Failed to record view", error);
      }
    };

    timeout = setTimeout(recordView, 150);
    return () => timeout && clearTimeout(timeout);
  }, [isDetailView, selectedThreadId, syncThreadCounts]);

  // The newest official post (today's Sound of the Day) stays pinned on top.
  const pinnedThreadId = useMemo(
    () => threads.find((thread) => thread.is_official)?.id ?? null,
    [threads]
  );

  const filteredThreads = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const matching = searchQuery
      ? threads.filter(
          (thread) =>
            thread.title.toLowerCase().includes(query) ||
            thread.body.toLowerCase().includes(query)
        )
      : threads;
    const pinned = matching.filter((thread) => thread.id === pinnedThreadId);
    return [...pinned, ...matching.filter((thread) => thread.id !== pinnedThreadId)];
  }, [threads, searchQuery, pinnedThreadId]);

  const handleThreadSubmit = async (event) => {
    event.preventDefault();
    setThreadFormError("");

    if (!threadForm.title.trim() || !threadForm.body.trim()) {
      setThreadFormError("Give your thread a title and a bit of context.");
      return;
    }

    setCreatingThread(true);
    try {
      const newThread = await ForumService.createThread({
        title: threadForm.title.trim(),
        body: threadForm.body.trim(),
      });
      setThreads((prev) => [newThread, ...prev]);
      setThreadForm({ title: "", body: "" });
      await handleSelectThread(newThread.id);
    } catch (error) {
      const serverMessage = error.response?.data?.error;
      console.error("Failed to create thread", serverMessage || error);
      setThreadFormError(
        serverMessage || "We couldn’t post that. Please try again."
      );
    } finally {
      setCreatingThread(false);
    }
  };

  const handleCommentSubmit = async (event) => {
    event.preventDefault();
    setCommentError("");

    if (!selectedThreadId) {
      setCommentError("Select a thread before commenting.");
      return;
    }

    if (!commentBody.trim()) {
      setCommentError("Comments can’t be empty.");
      return;
    }

    setCreatingComment(true);
    try {
      const newComment = await ForumService.createComment(
        selectedThreadId,
        commentBody.trim()
      );
      setCommentBody("");
      setSelectedThread((prev) =>
        prev
          ? { ...prev, comments: [...(prev.comments || []), newComment] }
          : prev
      );
      setThreads((prev) =>
        prev.map((thread) =>
          thread.id === selectedThreadId
            ? {
                ...thread,
                comment_count: (thread.comment_count || 0) + 1,
              }
            : thread
        )
      );
    } catch (error) {
      const serverMessage = error.response?.data?.error;
      console.error("Failed to add comment", serverMessage || error);
      setCommentError(
        serverMessage || "Unable to add that reply. Try again?"
      );
    } finally {
      setCreatingComment(false);
    }
  };

  const getThreadCount = useCallback(
    (threadId) =>
      threads.find((thread) => thread.id === threadId)?.upvote_count ??
      (selectedThread?.id === threadId
        ? selectedThread.upvote_count ?? 0
        : 0),
    [selectedThread, threads]
  );

  const refreshThreadData = useCallback(
    async (threadId) => {
      try {
        const updated = await ForumService.getThread(threadId);
        syncThreadCounts(threadId, {
          upvote_count: updated.upvote_count ?? 0,
          view_count: updated.view_count ?? 0,
          comment_count:
            updated.comments?.length ?? updated.comment_count ?? 0,
        });
        if (selectedThreadId === threadId && isDetailView) {
          setSelectedThread((prev) =>
            prev ? { ...prev, ...updated } : updated
          );
        }
      } catch (error) {
        console.error("Failed to refresh thread after upvote", error);
      }
    },
    [isDetailView, selectedThreadId, syncThreadCounts]
  );

  const handleThreadDelete = async (threadId) => {
    setDeletingThreadId(threadId);
    try {
      await ForumService.deleteThread(threadId);
      setThreads((prev) => prev.filter((thread) => thread.id !== threadId));
      if (selectedThreadId === threadId) {
        setSelectedThread(null);
        setSelectedThreadId(null);
        setIsDetailView(false);
      }
    } catch (error) {
      const serverMessage = error.response?.data?.error;
      console.error("Failed to delete thread", serverMessage || error);
      setThreadsError(
        serverMessage || "Unable to delete this thread. Try again later."
      );
    } finally {
      setDeletingThreadId(null);
    }
  };

  const handleThreadUpvote = async (threadId, event) => {
    if (event) {
      event.stopPropagation();
    }
    const previousCount = getThreadCount(threadId);
    const optimisticCount = previousCount + 1;
    syncThreadCounts(threadId, { upvote_count: optimisticCount });
    setHighlightedThreads((prev) => ({
      ...prev,
      [threadId]: true,
    }));
    setUpvotingThreadId(threadId);
    try {
      await ForumService.upvoteThread(threadId);
      await refreshThreadData(threadId);
    } catch (error) {
      const serverMessage = error.response?.data?.error;
      console.error("Failed to upvote thread", serverMessage || error);
      setThreadsError(
        serverMessage ||
          "Unable to upvote right now. Please try again in a moment."
      );
      syncThreadCounts(threadId, { upvote_count: previousCount });
      setHighlightedThreads((prev) => {
        const updated = { ...prev };
        delete updated[threadId];
        return updated;
      });
    } finally {
      setUpvotingThreadId(null);
    }
  };

  const handleCommentUpvote = async (commentId) => {
    const previousCount =
      selectedThread?.comments?.find((comment) => comment.id === commentId)
        ?.upvote_count ?? 0;
    const optimisticCount = previousCount + 1;
    setSelectedThread((prev) =>
      prev
        ? {
            ...prev,
            comments: prev.comments?.map((comment) =>
              comment.id === commentId
                ? { ...comment, upvote_count: optimisticCount }
                : comment
            ),
          }
        : prev
    );
    setHighlightedComments((prev) => ({
      ...prev,
      [commentId]: true,
    }));
    setUpvotingCommentId(commentId);
    try {
      const response = await ForumService.upvoteComment(commentId);
      if (response?.upvote_count !== undefined) {
        setSelectedThread((prev) =>
          prev
            ? {
                ...prev,
                comments: prev.comments?.map((comment) =>
                  comment.id === commentId
                    ? { ...comment, upvote_count: response.upvote_count }
                    : comment
                ),
              }
            : prev
        );
      }
    } catch (error) {
      const serverMessage = error.response?.data?.error;
      console.error("Failed to upvote comment", serverMessage || error);
      setThreadsError(
        serverMessage ||
          "Unable to upvote comments right now. Please try again."
      );
      setSelectedThread((prev) =>
        prev
          ? {
              ...prev,
              comments: prev.comments?.map((comment) =>
                comment.id === commentId
                  ? { ...comment, upvote_count: previousCount }
                  : comment
              ),
            }
          : prev
      );
      setHighlightedComments((prev) => {
        const updated = { ...prev };
        delete updated[commentId];
        return updated;
      });
    } finally {
      setUpvotingCommentId(null);
    }
  };

  const renderThreadList = () => (
    <section className="rounded-3xl border border-line bg-surface/80 p-3 shadow-panel backdrop-blur-2xl sm:p-6">
      <div className="space-y-4">
        <Input
          placeholder="Search discussions..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          className="rounded-2xl border border-line bg-elevated text-sm text-fg placeholder:text-fg-subtle"
        />
        {threadsError && (
          <p className="text-xs font-medium text-danger/90">
            {threadsError}
          </p>
        )}
        <div className="space-y-3">
          {threadsLoading ? (
            <p className="text-sm text-fg-muted">Loading threads…</p>
          ) : !filteredThreads.length ? (
            <p className="text-sm text-fg-muted">
              Nothing here yet—be the first to post!
            </p>
          ) : (
            filteredThreads.map((thread) => {
              const threadCreatedAt = formatRelativeTime(thread.created_at);
              const upvotes = thread.upvote_count ?? 0;
              const views = thread.view_count ?? 0;
              return (
                <div
                  key={thread.id}
                  onClick={() => handleSelectThread(thread.id)}
                  className={`w-full cursor-pointer rounded-2xl border bg-surface p-5 text-left transition hover:border-primary/30 hover:bg-elevated ${
                    thread.id === pinnedThreadId
                      ? "border-primary/30 border-l-2 border-l-primary"
                      : "border-line"
                  }`}
                >
                  <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-fg-muted">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-xs ${(thread.is_official ? OFFICIAL_AVATAR : getAvatarMeta(thread.author_display_name || "Learner")).colorClass}`}
                    >
                      {(thread.is_official ? OFFICIAL_AVATAR : getAvatarMeta(thread.author_display_name || "Learner")).displayLetter}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{thread.author_display_name || "Learner"}</span>
                      {thread.is_official && <OfficialBadge />}
                      {thread.id === pinnedThreadId && (
                        <span className="normal-case tracking-normal text-accent">📌 Pinned</span>
                      )}
                      <span className="h-1 w-1 rounded-full bg-primary/10" />
                      <span className="normal-case">
                        {threadCreatedAt || "Just now"}
                      </span>
                    </div>
                  </div>
                  <h3 className="mt-3 text-lg font-semibold text-fg">
                    {thread.title}
                  </h3>
                  <p className="mt-1 line-clamp-2 text-sm text-fg-muted">
                    {thread.body}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-xs font-semibold uppercase tracking-[0.3em] text-primary/80">
                    <div className="inline-flex gap-3 text-primary/80">
                      <button
                        type="button"
                        onClick={(event) => handleThreadUpvote(thread.id, event)}
                        disabled={upvotingThreadId === thread.id}
                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.3em] transition disabled:opacity-50 ${
                          highlightedThreads[thread.id]
                            ? "bg-primary/90 text-primary-fg"
                            : "bg-transparent text-primary/80 hover:bg-primary/20"
                        }`}
                      >
                        <ThumbsUp className="h-3.5 w-3.5" />
                        {upvotes}
                      </button>
                      <span className="inline-flex items-center gap-1">
                        <MessageCircle className="h-3.5 w-3.5" />
                        {thread.comment_count || 0}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Eye className="h-3.5 w-3.5" />
                        {views}
                      </span>
                    </div>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleThreadDelete(thread.id);
                        }}
                        disabled={deletingThreadId === thread.id}
                        className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-full bg-danger/10 text-danger hover:bg-danger/20 disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );

  const renderDetailView = () => (
    <section className="rounded-3xl border border-line bg-surface p-4 shadow-panel sm:p-6">
      <div className="mb-6 flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            setIsDetailView(false);
            setSelectedThread(null);
            setSelectedThreadId(null);
          }}
          className="inline-flex items-center gap-2 rounded-full border border-line px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-fg transition hover:bg-elevated"
        >
          ← Back
        </button>
      </div>
      {selectedLoading ? (
        <p className="text-sm text-fg-muted">Loading thread…</p>
      ) : !selectedThread ? (
        <p className="text-sm text-fg-muted">
          Select a thread to read the conversation.
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="space-y-4">
                <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-fg-muted">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full text-sm ${(selectedThread.is_official ? OFFICIAL_AVATAR : getAvatarMeta(selectedThread.author_display_name || "Learner")).colorClass}`}
                  >
                    {(selectedThread.is_official ? OFFICIAL_AVATAR : getAvatarMeta(selectedThread.author_display_name || "Learner")).displayLetter}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span>{selectedThread.author_display_name || "Learner"}</span>
                    {selectedThread.is_official && <OfficialBadge />}
                    <span className="h-1 w-1 rounded-full bg-primary/10" />
                    <span className="normal-case">
                      {formatRelativeTime(selectedThread.created_at)}
                    </span>
                  </div>
                </div>
            <h2 className="text-3xl font-semibold leading-tight text-fg">
              {selectedThread.title}
            </h2>
            <p className="whitespace-pre-line text-sm text-fg-muted">
              {selectedThread.body}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-xs font-semibold uppercase tracking-[0.3em] text-primary/80">
              <div className="inline-flex flex-wrap items-center gap-4 text-primary/80">
                <button
                  type="button"
                  disabled={upvotingThreadId === selectedThread.id}
                  className={`inline-flex items-center gap-1 rounded-full px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.3em] transition disabled:opacity-50 ${
                    highlightedThreads[selectedThread.id]
                      ? "bg-primary/90 text-primary-fg"
                      : "bg-transparent text-fg hover:bg-elevated"
                  }`}
                  onClick={() => handleThreadUpvote(selectedThread.id)}
                >
                  <ThumbsUp className="h-4 w-4" />
                  {selectedThread.upvote_count ?? 0}
                </button>
                <span className="inline-flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  {selectedThread.comments?.length || 0}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  {selectedThread.view_count ?? 0}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-primary/80">
              Replies
            </h3>
            <div className="space-y-3">
              {selectedThread.comments && selectedThread.comments.length ? (
                selectedThread.comments.map((comment) => {
                  const { displayLetter, colorClass } = comment.is_official
                    ? OFFICIAL_AVATAR
                    : getAvatarMeta(comment.author_display_name || "Learner");
                  return (
                    <div
                      key={comment.id}
                      className="flex gap-4 rounded-2xl border border-line bg-surface/80 p-4 text-sm text-fg"
                    >
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-xs ${colorClass}`}
                      >
                        {displayLetter}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-fg-muted">
                          <span>{comment.author_display_name || "Learner"}</span>
                          {comment.is_official && <OfficialBadge />}
                          <span className="h-1 w-1 rounded-full bg-primary/10" />
                          <span className="normal-case">
                            {formatRelativeTime(comment.created_at)}
                          </span>
                        </div>
                        <p className="whitespace-pre-line text-fg">
                          {comment.body}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-fg-muted">
                  No replies yet. Start the conversation!
                </p>
              )}
            </div>
          </div>

          <form className="space-y-3" onSubmit={handleCommentSubmit}>
            <Textarea
              placeholder="Write a reply…"
              value={commentBody}
              onChange={(event) => setCommentBody(event.target.value)}
              rows={3}
              className="rounded-2xl border border-line bg-elevated text-sm text-fg placeholder:text-fg-subtle"
            />
            {commentError && (
              <p className="text-xs font-medium text-danger/90">
                {commentError}
              </p>
            )}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={creatingComment || !selectedThreadId}
                className="rounded-full border border-line bg-transparent px-6 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-fg hover:bg-elevated disabled:opacity-50"
              >
                {creatingComment ? "Posting…" : "Post reply"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );

  return (
    <PageContainer innerClassName="max-w-6xl gap-8">
      <header className="flex flex-col gap-4">
        <span className="inline-flex w-fit items-center gap-2 rounded-full border border-line bg-surface/80 px-4 py-1 text-[10px] uppercase tracking-[0.4em] text-primary/80">
          SpeechCraft Community
        </span>
        <div className="space-y-3">
          <h1 className="text-4xl font-semibold tracking-tight text-fg sm:text-5xl">
            Forum
          </h1>
          <p className="max-w-2xl text-sm text-fg-muted">
            Share study tips, pronunciation wins, or ask for feedback. Threads
            update in real time so you can keep the conversation flowing.
          </p>
        </div>
      </header>

      <section className="rounded-3xl border border-line bg-surface/80 p-3 shadow-panel backdrop-blur-2xl sm:p-6">
        <div className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-4 sm:p-5">
          <div>
            <h2 className="text-xl font-semibold text-fg">
              Start a conversation
            </h2>
            <p className="text-sm text-fg-muted">
              Ask a question, share a win, or start a practice thread.
            </p>
          </div>
          <form className="space-y-3" onSubmit={handleThreadSubmit}>
            <Input
              placeholder="Thread title"
              value={threadForm.title}
              onChange={(event) =>
                setThreadForm((prev) => ({
                  ...prev,
                  title: event.target.value,
                }))
              }
              className="w-full rounded-2xl border border-line bg-elevated text-sm text-fg placeholder:text-fg-subtle"
            />
            <Textarea
              placeholder="What would you like to talk about?"
              value={threadForm.body}
              onChange={(event) =>
                setThreadForm((prev) => ({
                  ...prev,
                  body: event.target.value,
                }))
              }
              rows={3}
              className="rounded-2xl border border-line bg-elevated text-sm text-fg placeholder:text-fg-subtle"
            />
            {threadFormError && (
              <p className="text-xs font-medium text-danger/90">
                {threadFormError}
              </p>
            )}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={creatingThread}
                className="rounded-full bg-primary/90 px-6 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-primary-fg shadow-panel disabled:opacity-50"
              >
                {creatingThread ? "Posting…" : "Post thread"}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {isDetailView ? renderDetailView() : renderThreadList()}
    </PageContainer>
  );
};

export default Forum;
